import { ReactNode, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import { useTranslation } from 'react-i18next';
import { AssetModel, SystemTypePpu } from '@customer/store/apis/assetsApi';
import AssetPanelConfiguration from './configuration/AssetPanelConfiguration';
import { AssetInteractionsAccordion, closeSidePanel } from '@customer/store/slices/globalSlice';
import { useNavigate } from 'react-router-dom';
import AssetPanelDetails from './details/AssetPanelDetails';
import AssetPanelSelfTestSummary from './selftest-summary/AssetPanelSelfTestSummary';
import { useAssetOverviewErrors } from '@customer/hooks/assets/useAssetOverviewErrors';
import useTabs, { TabConfig, TabModel } from '@cas/components/tabs/tabs/useTabs';
import AssetPanelEvents from './interactions/AssetPanelInteractions';
import { Permission, userHasPermission } from '@customer/permissions';

interface Return {
  tabsProps: {
    tabs: TabModel[];
    activeTab?: number;
    onSelectTab: (index: number) => void;
  };
  btnProps?: {
    onClick: () => void;
    children: ReactNode;
  };
  activeView: ReactNode;
}

export const useSingleAssetPanelNavigation = (asset?: AssetModel): Return => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const globalState = useAppSelector(state => state.global);
  const userData = useAppSelector(state => state.auth);
  const globalData = useAppSelector(state => state.global);

  const assetOverviewErrors = useAssetOverviewErrors({ asset });

  const tabConfigs = useMemo<TabConfig[]>(() => {
    if (asset === undefined) {
      return [];
    }

    const items: TabConfig[] = [];

    if (asset?.hw !== SystemTypePpu) {
      items.push({
        title: t('SelfTest'),
        view: <AssetPanelSelfTestSummary asset={asset} />,
        isDotted: assetOverviewErrors.levelOneFaultsActive,
      });
    }

    items.push({
      title: t('Configuration'),
      view: <AssetPanelConfiguration asset={asset} />,
      isDotted: assetOverviewErrors.config,
    });

    items.push({
      title: t('AssetDetails'),
      view: <AssetPanelDetails asset={asset} />,
      isDotted: assetOverviewErrors.reporting,
    });

    if (userHasPermission(userData, Permission.PerformanceRead)) {
      items.push({
        title: t('Interactions'),
        view: <AssetPanelEvents asset={asset} />,
        isDotted: assetOverviewErrors.interactions,
      });
    }

    return items;
  }, [
    asset,
    assetOverviewErrors.config,
    assetOverviewErrors.interactions,
    assetOverviewErrors.levelOneFaultsActive,
    assetOverviewErrors.reporting,
    t,
    userData,
  ]);

  const { tabs, activeTab, activeView, onSelectTab } = useTabs({
    tabConfigs,
    defaultTab: globalState.sidePanelTab,
  });

  const showDefaultButton = !asset || asset.hw === SystemTypePpu;

  type ButtonData = [string, string];

  const buttonsData: ButtonData[] = asset
    ? [
        ...(asset.hw !== SystemTypePpu ? [[t('MoreDetails'), 'self-test'] as ButtonData] : []),
        [t('MoreDetails'), 'configuration'],
        [t('MoreDetails'), 'details'],
        [t('MoreDetails'), 'interactions'],
      ]
    : [];

  const defaultButtonData: ButtonData = [t('MoreDetails'), 'details'];

  const [buttonDataIndex, setButtonDataIndex] = useState<number | undefined>();

  if (
    typeof buttonDataIndex === 'undefined' &&
    typeof activeTab !== 'undefined' &&
    !showDefaultButton
  ) {
    setButtonDataIndex(activeTab);
  }

  const [buttonText, assetPageSubPath] =
    typeof buttonDataIndex !== 'undefined' ? buttonsData[buttonDataIndex] : defaultButtonData;

  // Override the normal onSelectTab callback to update the footer button when the tab is changed.
  const onSelectAssetTab = (index: number) => {
    onSelectTab(index);
    if (!showDefaultButton) {
      setButtonDataIndex(index);
    }
  };

  const buttonOnClick = () => {
    dispatch(closeSidePanel());

    if (asset) {
      let tabPath = '';
      if (globalData.sidePanelDefaultAccordion === AssetInteractionsAccordion.VehicleInteractions) {
        tabPath = '/vehicle-interactions';
      } else if (
        globalData.sidePanelDefaultAccordion === AssetInteractionsAccordion.GeofenceInteractions
      ) {
        tabPath = '/geofence-interactions';
      }

      navigate(`/asset/${asset.id}/${assetPageSubPath}${tabPath}`);
    }
  };

  return {
    tabsProps: {
      tabs,
      activeTab,
      onSelectTab: onSelectAssetTab,
    },
    btnProps: {
      onClick: buttonOnClick,
      children: buttonText,
    },
    activeView,
  };
};

export default useSingleAssetPanelNavigation;
